import React, { useEffect, useState } from 'react';
import { StyleProp, TextStyle } from 'react-native';
import FilterDropdown from '../Filter/components/FilterDropdown';
import { translate } from '../../services/translate';
import { dispatchService, SimplifiedEquipments } from '../../services/central-api/dispatch';
import { UseFormReturn } from 'react-hook-form';
import FormDropDownPicker from '../FormDropDownPicker';

type Props = {
    actingBodyId: number | null;
    zIndex: number;
    disabled?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    viewStyle?: StyleProp<TextStyle>;
    removeItemsWithoutSlots?: boolean;
    allowEmpty?: boolean;
    selectableEquipments?: SimplifiedEquipments[];
    setMaxValue?: React.Dispatch<React.SetStateAction<number>>;
    fieldMaxValue?: string;
} & ({
    form?: undefined;
    fieldName?: undefined;
    value: number;
    setValue: React.Dispatch<React.SetStateAction<number>>;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    value?: undefined;
    setValue?: undefined;
});

function EquipmentCategoryDropdown({ form, fieldName, fieldMaxValue, value, setValue, setMaxValue, actingBodyId, disabled, zIndex, labelStyle, viewStyle, removeItemsWithoutSlots, allowEmpty, selectableEquipments }: Props) {
    const [selectableEquipmentCategories, setSelectableEquipmentCategories] = useState<{ label: string; value: number; }[]>([]);
    const [equipmentCategories, setEquipmentCategories] = useState<{ label: string; value: number; maxValue: number; }[]>([]);

    async function getEquipments({ actingBodyId }: { actingBodyId: number; }) {
        try {
            let equipmentCategories = [];
            if (selectableEquipments) {
                const equipmentsWithCategory = selectableEquipments.filter(e => e.equipmentCategory);

                const counting = equipmentsWithCategory.reduce((acc, el) => {
                    if (!el.equipmentCategory || !el.id) {
                        return acc;
                    }

                    const categoryName = el.equipmentCategory.name;
                    if (!acc[categoryName]) {
                        acc[categoryName] = { id: el.equipmentCategory.id, available: 0 };
                    }

                    acc[categoryName].available++;

                    return acc;
                }, {} as Record<string, { id: number; available: number; }>);

                const itensForList = Object.entries(counting).map(([name, data]) => ({
                    name,
                    id: data.id,
                    available: data.available
                }));

                equipmentCategories = [...itensForList];
            } else {
                equipmentCategories = await dispatchService.getEquipmentCategorySimplified(actingBodyId);
            }

            setSelectableEquipmentCategories([
                ...(allowEmpty ? [{ value: 0, label: '' }] : []),
                ...equipmentCategories
                    .filter(item => {
                        if (removeItemsWithoutSlots && item.available === 0) {
                            return false;
                        }
                        return true;
                    })
                    .map((item) => {
                        return {
                            label: item.name, value: item.id
                        };
                    })]);
            setEquipmentCategories([
                ...(allowEmpty ? [{ value: 0, label: '', maxValue: 0 }] : []),
                ...equipmentCategories.map((item) => {
                    return {
                        label: item.name, value: item.id, maxValue: item.available
                    };
                })
            ]);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (actingBodyId) {
            getEquipments({ actingBodyId });
        }
    }, [actingBodyId]);

    useEffect(() => {
        const item = equipmentCategories.find(i => i.value === value);
        if (setMaxValue) {
            setMaxValue(item ? item.maxValue : 100000);
        } else if (form && fieldMaxValue) {
            form.setValue('maxValue', item ? item.maxValue : 100000);
        }
    }, [value]);

    function shouldShow() {
        if (allowEmpty) {
            return equipmentCategories.length > 1;
        }

        return equipmentCategories.length > 0;
    }
    if (shouldShow()) {
        if (form && fieldName) {
            return <FormDropDownPicker
                label={translate('equipmentCategory')}
                items={selectableEquipmentCategories}
                multiple={false}
                zIndex={zIndex}
                disabled={disabled}
                fieldName={fieldName}
                form={form}
                labelStyle={labelStyle}
                viewStyle={viewStyle}
            />;
        } else if (value !== undefined && setValue !== undefined) {
            return <FilterDropdown
                label={translate('equipmentCategory')}
                items={selectableEquipmentCategories}
                setValue={setValue}
                value={value}
                disabled={disabled}
                zIndex={zIndex}
                labelStyle={labelStyle}
                viewStyle={viewStyle}
            />;
        }
    } else {
        return <></>;
    }
}

export default EquipmentCategoryDropdown;
